/**
 * "Your priorities": the ecolabels a shopper has said matter most to them.
 *
 * Kept in localStorage so it works without an account, and mirrored to the
 * shopper's private profile data when they're logged in (see
 * hooks/useValuePriorities.js). This module is a tiny store that React reads
 * with useSyncExternalStore, so every listing card updates together when the
 * shopper changes their priorities, and other tabs follow via the storage event.
 */

import { getEcolabelsByKeys } from '../config/ecolabels';

const STORAGE_KEY = 'ethy-value-priorities-v1';

// The key used in currentUser.attributes.profile.privateData.
export const VALUE_PRIORITIES_ACCOUNT_KEY = 'valuePriorities';

// One shared empty array, so "no priorities" is always the same snapshot.
const EMPTY = [];

let current = null;
const listeners = new Set();

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

/**
 * Reduce anything to a list of known ecolabel keys, in canonical order.
 *
 * @param {*} values stored or selected values
 * @returns {Array<string>}
 */
export const sanitiseValuePriorities = values => {
  const keys = getEcolabelsByKeys(Array.isArray(values) ? values : []).map(e => e.key);
  return keys.length > 0 ? keys : EMPTY;
};

const isSameList = (a, b) => a.length === b.length && a.every((key, i) => key === b[i]);

const load = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return sanitiseValuePriorities(raw ? JSON.parse(raw) : EMPTY);
  } catch (e) {
    return EMPTY;
  }
};

const persist = keys => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch (e) {
    // Private mode / quota: the priorities still last for this visit.
  }
};

const notify = () => listeners.forEach(listener => listener());

/**
 * The shopper's current priorities (ecolabel keys). Always empty on the server.
 *
 * @returns {Array<string>}
 */
export const getValuePriorities = () => {
  if (!canUseStorage()) {
    return EMPTY;
  }
  if (current === null) {
    current = load();
  }
  return current;
};

// What the server renders, and what the client hydrates with before reading storage.
export const getServerValuePriorities = () => EMPTY;

/**
 * Replace the shopper's priorities.
 *
 * @param {Array<string>} values ecolabel keys or option values
 * @returns {Array<string>} the priorities as saved
 */
export const setValuePriorities = values => {
  const next = sanitiseValuePriorities(values);
  if (isSameList(next, getValuePriorities())) {
    return getValuePriorities();
  }
  current = next;
  if (canUseStorage()) {
    persist(next);
  }
  notify();
  return next;
};

// Another tab changed the priorities: read them again.
const handleStorage = event => {
  if (event.key === STORAGE_KEY || event.key === null) {
    current = null;
    notify();
  }
};

/**
 * Subscribe to changes, in the shape useSyncExternalStore expects.
 *
 * @param {Function} listener
 * @returns {Function} unsubscribe
 */
export const subscribeToValuePriorities = listener => {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
};

/**
 * Which of the shopper's priorities a product carries.
 *
 * @param {Array<string>} priorities ecolabel keys the shopper chose
 * @param {Array<string>} ecolabelKeys ecolabel keys the product has been awarded
 * @returns {{matched: Array<string>, total: number}}
 */
export const matchValuePriorities = (priorities = [], ecolabelKeys = []) => ({
  matched: priorities.filter(key => ecolabelKeys.includes(key)),
  total: priorities.length,
});
